// ============================================
// FILE: src/components/fashion/ProductRail.jsx
// ============================================
//
// Horizontally scrolling strip of product cards, used for the "Similar
// Products" and "Recently Viewed" sections on the product page.
//
// Renders nothing at all when there is no data — an empty rail with a heading
// reads as a broken page, so a short catalogue simply shows fewer sections.

import React from 'react';
import { Percent } from 'lucide-react';
import FashionProductCard from './FashionProductCard';
import DealCard from './DealCard';

const ProductRail = ({
  title,
  products = [],
  onOpen = () => {},
  toggleFavorite,
  favorites = [],
  isDesktop = false,
  loading = false,
  // The scroller bleeds to the container edge so cards can scroll past it.
  // The default assumes a 4-unit gutter; callers in differently-padded
  // containers pass their own, or '' to sit flush.
  bleedClass = '-mx-4 px-4',
  className = 'mt-10',
  // 'deal' swaps in the compact discount-led card and an accented heading, so
  // the offers shelf does not look like a second copy of the grid below it.
  variant = 'default',
  subtitle = null,
}) => {
  if (!loading && (!Array.isArray(products) || products.length === 0)) {
    return null;
  }

  const isDeal = variant === 'deal';
  const CardComponent = isDeal ? DealCard : FashionProductCard;

  // Every rail uses the same compact square card, so the shelves line up and
  // more of each is visible without scrolling. The category grid below keeps
  // its taller 3:4 cards.
  const cardWidth = isDesktop ? 168 : 132;

  const header = isDeal ? (
    <div className="flex items-center gap-2 mb-3">
      <span
        className="inline-flex items-center justify-center"
        style={{ width: 24, height: 24, borderRadius: 4, background: 'var(--fx-pink)' }}
      >
        <Percent size={13} className="text-white" strokeWidth={3} />
      </span>
      <span className="fx-label" style={{ color: 'var(--fx-pink)' }}>{title}</span>
      {subtitle && (
        <span style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>{subtitle}</span>
      )}
    </div>
  ) : (
    <h3 className="fx-label mb-4">{title}</h3>
  );

  return (
    <section className={className}>
      {header}

      <div className={`flex gap-3 overflow-x-auto hide-scrollbar pb-1 ${bleedClass}`}>
        {loading
          ? Array.from({ length: isDesktop ? 5 : 3 }).map((_, i) => (
              <div key={i} className="flex-shrink-0" style={{ width: cardWidth }}>
                <div
                  className="w-full bg-[#f5f5f6] animate-pulse"
                  style={{ aspectRatio: '1 / 1' }}
                />
                <div className="pt-2 space-y-2">
                  <div className="h-3 bg-[#f5f5f6] w-2/3 animate-pulse" />
                  <div className="h-3 bg-[#f5f5f6] w-full animate-pulse" />
                  <div className="h-4 bg-[#f5f5f6] w-1/2 animate-pulse" />
                </div>
              </div>
            ))
          : products.map((product, index) => (
              <div
                key={`${product.id}-${index}`}
                className="flex-shrink-0"
                style={{ width: cardWidth }}
              >
                <CardComponent
                  product={product}
                  index={index}
                  onOpen={onOpen}
                  toggleFavorite={toggleFavorite}
                  favorites={favorites}
                  isDesktop={isDesktop}
                  compact
                />
              </div>
            ))}
      </div>
    </section>
  );
};

export default ProductRail;
